import type {
  ApiResponse,
  AuthResponse,
  AvailabilitySearchRequest,
  AvailabilitySearchResponse,
  BookingConfirmation,
  BookingIdentityRequest,
  BookingIntentRequest,
  BookingIntentResponse,
  BookingPaymentSession,
  Faq,
  FaqInput,
  FeaturedRoomsResponse,
  GalleryItem,
  GalleryItemInput,
  MemberBookingsResponse,
  MemberConfirmationResend,
  MemberEmailConfirmation,
  MemberPasswordChange,
  MemberProfile,
  MemberProfileUpdate,
  MemberRegisterRequest,
  Membership,
  PublicUser,
  RefreshResponse,
  SecretsEvent,
  SecretsGalleryImage,
  SecretsNewsArticle,
  Slide,
  SlideInput,
  VendorHotelRecord,
} from '@secretsoverflow/types';

/** An admin-panel account. `PublicUser` narrowed to the staff roles. */
export interface StaffUser {
  id: string;
  email: string;
  firstName: string | null;
  lastName: string | null;
  role: 'admin' | 'editor';
  createdAt: string;
  updatedAt: string;
}

export const API_URL: string = import.meta.env.VITE_API_URL ?? 'http://localhost:4000';

export class ApiClientError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details?: Record<string, string[]>;

  constructor(status: number, code: string, message: string, details?: Record<string, string[]>) {
    super(message);
    this.name = 'ApiClientError';
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

/** In-memory access token (never persisted to storage). */
let accessToken: string | null = null;

export const setAccessToken = (token: string | null): void => {
  accessToken = token;
};
export const getAccessToken = (): string | null => accessToken;

interface RequestOptions {
  method?: string;
  body?: unknown;
  /** Send the httpOnly refresh cookie (auth endpoints). */
  auth?: boolean;
  /** Attach the bearer access token. */
  bearer?: boolean;
  signal?: AbortSignal;
}

async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const headers: Record<string, string> = {};
  if (options.body !== undefined) headers['Content-Type'] = 'application/json';
  if (options.bearer && accessToken) headers.Authorization = `Bearer ${accessToken}`;

  const res = await fetch(`${API_URL}${path}`, {
    method: options.method ?? 'GET',
    headers,
    body: options.body !== undefined ? JSON.stringify(options.body) : undefined,
    credentials: options.auth ? 'include' : 'same-origin',
    signal: options.signal,
  });

  const payload = (await res.json().catch(() => null)) as ApiResponse<T> | null;

  if (!payload) {
    throw new ApiClientError(res.status, 'network_error', 'Empty or invalid response');
  }
  if (!payload.ok) {
    throw new ApiClientError(
      res.status,
      payload.error.code,
      payload.error.message,
      payload.error.details,
    );
  }
  return payload.data;
}

/** Build the availability query string (origin is always Secrets Hideaway). */
function availabilityQuery(params: AvailabilitySearchRequest): string {
  return new URLSearchParams({
    radiusKm: String(params.radiusKm),
    checkIn: params.checkIn,
    checkOut: params.checkOut,
    guests: String(params.guests),
  }).toString();
}

/** POST a file as the raw request body (the avatar endpoint takes one image). */
async function requestRaw<T>(path: string, file: File): Promise<T> {
  const headers: Record<string, string> = { 'Content-Type': file.type };
  if (accessToken) headers.Authorization = `Bearer ${accessToken}`;

  const res = await fetch(`${API_URL}${path}`, {
    method: 'POST',
    headers,
    body: file,
    credentials: 'same-origin',
  });

  const payload = (await res.json().catch(() => null)) as ApiResponse<T> | null;
  if (!payload) throw new ApiClientError(res.status, 'network_error', 'Empty or invalid response');
  if (!payload.ok) {
    throw new ApiClientError(
      res.status,
      payload.error.code,
      payload.error.message,
      payload.error.details,
    );
  }
  return payload.data;
}

export const api = {
  availability: {
    search: (params: AvailabilitySearchRequest, signal?: AbortSignal) =>
      request<AvailabilitySearchResponse>(`/v1/availability?${availabilityQuery(params)}`, {
        signal,
      }),
    // The nearby-hotel showcase, cached server-side on a slow cadence.
    featured: (signal?: AbortSignal) =>
      request<FeaturedRoomsResponse>('/v1/availability/featured', { signal }),
  },
  // A booking is recorded when a room is picked, before anyone has identified
  // themselves, so an abandoned checkout still leaves us the search behind it.
  // Both calls send the bearer token when there is one, which is what links the
  // record to a signed-in member.
  bookings: {
    start: (body: BookingIntentRequest) =>
      request<BookingIntentResponse>('/v1/bookings', { method: 'POST', body, bearer: true }),
    identify: (id: string, body: BookingIdentityRequest) =>
      request<{ recorded: boolean }>(`/v1/bookings/${id}/identity`, {
        method: 'POST',
        body,
        bearer: true,
      }),
    // Holds the room and returns what the vendor's payment SDK needs. The
    // client secret is used immediately and never stored.
    payment: (id: string) =>
      request<BookingPaymentSession>(`/v1/bookings/${id}/payment`, {
        method: 'POST',
        bearer: true,
      }),
    // Places the reservation. Idempotent server-side, so a double submit or a
    // reload returns the existing confirmation rather than booking twice.
    confirm: (id: string) =>
      request<BookingConfirmation>(`/v1/bookings/${id}/confirm`, {
        method: 'POST',
        bearer: true,
      }),
  },
  // Members authenticate against the Secrets API via our backend, which then
  // issues one of our sessions. Refresh and logout are shared with `auth`.
  member: {
    login: (body: { email: string; password: string }) =>
      request<{ member: MemberProfile; accessToken: string }>('/v1/member/login', {
        method: 'POST',
        body,
        auth: true,
      }),
    register: (body: MemberRegisterRequest) =>
      request<{ member: MemberProfile; accessToken: string | null; pendingReason: string | null }>(
        '/v1/member/register',
        { method: 'POST', body, auth: true },
      ),
    me: () => request<MemberProfile>('/v1/member/me', { bearer: true }),
    bookings: (signal?: AbortSignal) =>
      request<MemberBookingsResponse>('/v1/member/me/bookings', { bearer: true, signal }),
    update: (body: MemberProfileUpdate) =>
      request<MemberProfile>('/v1/member/me', { method: 'PUT', body, bearer: true }),
    changePassword: (body: MemberPasswordChange) =>
      request<{ changed: boolean }>('/v1/member/me/password', {
        method: 'PUT',
        body,
        bearer: true,
      }),
    uploadAvatar: (file: File) => requestRaw<MemberProfile>('/v1/member/me/avatar', file),
    /**
     * Cancel one of the caller's own bookings. The API matches the id against
     * the session holder's own stays, so someone else's simply is not found,
     * and it refuses a non-refundable rate or a stay that has already started
     * rather than taking a fee this page cannot quote.
     */
    cancelBooking: (id: string) =>
      request<{
        status: string;
        cancellation: {
          status: string;
          cancellationFeeCents: number | null;
          refundAmountCents: number | null;
          currency: string | null;
        };
        guestNotified: boolean;
      }>(`/v1/member/bookings/${id}/cancel`, { method: 'POST', bearer: true }),
    // Both public: somebody who has forgotten their password has no session.
    requestPasswordReset: (email: string) =>
      request<{ sent: boolean }>('/v1/member/password-reset', {
        method: 'POST',
        body: { email },
        auth: true,
      }),
    confirmPasswordReset: (token: string, password: string) =>
      request<{ reset: boolean }>('/v1/member/password-reset/confirm', {
        method: 'POST',
        body: { token, password },
        auth: true,
      }),
    /** Redeem the link from a confirmation email. Needs no session: the token is the proof. */
    confirmEmail: (token: string) =>
      request<MemberEmailConfirmation>('/v1/member/confirm-email', {
        method: 'POST',
        body: { token },
        auth: true,
      }),
    /** Send the signed-in member another confirmation link. Once a minute at most. */
    resendConfirmation: () =>
      request<MemberConfirmationResend>('/v1/member/me/confirmation-email', {
        method: 'POST',
        bearer: true,
      }),
  },
  auth: {
    register: (body: { email: string; password: string; firstName?: string; lastName?: string }) =>
      request<AuthResponse>('/v1/auth/register', { method: 'POST', body, auth: true }),
    login: (body: { email: string; password: string }) =>
      request<AuthResponse>('/v1/auth/login', { method: 'POST', body, auth: true }),
    refresh: () => request<RefreshResponse>('/v1/auth/refresh', { method: 'POST', auth: true }),
    logout: () => request<{ loggedOut: boolean }>('/v1/auth/logout', { method: 'POST', auth: true }),
    me: () => request<PublicUser>('/v1/auth/me', { bearer: true }),
  },
  membership: {
    me: () => request<Membership>('/v1/membership/me', { bearer: true }),
  },
  admin: {
    // Admin-panel accounts. Separate from `members`, which are shadow rows for
    // secretsfl.com guests whose password lives in the Secrets API.
    staff: {
      list: () => request<{ items: StaffUser[] }>('/v1/admin/users', { bearer: true }),
      create: (body: {
        email: string;
        password: string;
        role: 'admin' | 'editor';
        firstName?: string;
        lastName?: string;
      }) => request<StaffUser>('/v1/admin/users', { method: 'POST', body, bearer: true }),
      update: (id: string, body: { role?: 'admin' | 'editor'; password?: string }) =>
        request<StaffUser>(`/v1/admin/users/${id}`, { method: 'PATCH', body, bearer: true }),
      remove: (id: string) =>
        request<{ deleted: boolean }>(`/v1/admin/users/${id}`, { method: 'DELETE', bearer: true }),
    },
    overview: () =>
      request<{
        members: {
          /** Members only. Panel accounts are counted separately. */
          total: number;
          admins: number;
          members: number;
          /** Admins + editors. Optional: added after the field above. */
          staff?: number;
        };
        memberships: { total: number; active: number };
        bookings: {
          total: number;
          confirmed: number;
          started: number;
          /** The vendor refused the book. What became of any payment is unknown. */
          failed: number;
          revenueCents: number;
          /** Null when confirmed bookings span more than one currency. */
          revenueCurrency: string | null;
          guests: number;
          members: number;
          withIdentity: number;
        };
        bookingsByDay: Array<{ day: string; started: number; confirmed: number }>;
        /**
         * How many bookings currently disagree with the vendor, from the verdicts
         * the drift sweep persisted. OPTIONAL for the same deploy-skew reason as
         * vendorBookings below, and here it matters twice over, because the
         * fallback while the API catches up must never be a reassuring zero.
         */
        drift?: {
          critical: number;
          warning: number;
          /** Compared, nothing found. The denominator that makes the others readable. */
          clear: number;
          /** critical + warning + clear. Zero means the sweep has never landed. */
          checked: number;
          /** Reached the vendor, never compared. Not a clean bill of health. */
          neverChecked: number;
          lastCheckedAt: string | null;
        };
        /**
         * Everything that reached the vendor, for cross-checking against Nuitee.
         *
         * OPTIONAL on purpose, and it must stay that way. Web and API deploy
         * independently and the web bundle lands first by several minutes, so
         * between the two every newly-added field is genuinely absent from the
         * responses the new page receives. Typing it as required let a `.length`
         * through that took the whole admin dashboard down in production.
         */
        vendorBookings?: Array<{
          id: string;
          status: string;
          guestEmail: string | null;
          guestName: string;
          hotelName: string;
          checkIn: string;
          totalPriceCents: number;
          currency: string;
          prebookedAt: string | null;
          settledAt: string | null;
          vendorBookingId: string | null;
          vendorConfirmationCode: string | null;
          vendorPrebookId: string | null;
          vendorTransactionId: string | null;
          vendorPaymentIntentId: string | null;
          /** What the last comparison against the vendor found. Optional: added later. */
          driftSeverity?: string | null;
          driftCode?: string | null;
          driftLabel?: string | null;
          driftCheckedAt?: string | null;
        }>;
        topHotels: Array<{
          hotelName: string;
          city: string | null;
          bookings: number;
          confirmed: number;
          avgNightlyCents: number;
        }>;
        stayDemand: Array<{ date: string; searched: number; booked: number; confirmed: number }>;
        cache: { total: number; fresh: number; stale: number; provider: string };
        searchesByDay: Array<{ day: string; count: number }>;
      }>('/v1/admin/overview', { bearer: true }),
    /**
     * One booking as we hold it, beside the vendor's own record, with the
     * disagreements named. A live vendor call, so it is fetched per row on
     * demand and never for the table.
     */
    reconcileBooking: (id: string) =>
      request<{
        ours: {
          id: string;
          status: string;
          guestName: string;
          guestEmail: string | null;
          hotelName: string;
          checkIn: string;
          checkOut: string;
          totalPriceCents: number;
          currency: string;
          vendorBookingId: string | null;
          vendorConfirmationCode: string | null;
          vendorPrebookId: string | null;
          vendorTransactionId: string | null;
          vendorPaymentIntentId: string | null;
          prebookedAt: string | null;
          paidAt: string | null;
          confirmedAt: string | null;
          createdAt: string | null;
          updatedAt: string | null;
        };
        /** Null when the vendor holds no such booking, which is normal for a failed row. */
        vendor: {
          vendorBookingId: string;
          status: string;
          confirmationCode: string | null;
          totalPriceCents: number | null;
          currency: string | null;
          clientReference?: string | null;
          commissionCents?: number | null;
          paymentStatus?: string | null;
          paymentTransactionId?: string | null;
          prebookId?: string | null;
          checkIn?: string | null;
          checkOut?: string | null;
          hotelId?: string | null;
          hotelName?: string | null;
          createdAt?: string | null;
          cancelledAt?: string | null;
          refundedAt?: string | null;
          refundedCents?: number | null;
          refundable?: boolean | null;
          freeCancellationUntil?: string | null;
          sandbox?: boolean | null;
        } | null;
        lookup: 'vendor_id' | 'client_reference' | 'never_sent' | 'unsupported';
        provider: string;
        environment: 'sandbox' | 'live';
        drift: Array<{
          code: string;
          severity: 'critical' | 'warning' | 'info';
          label: string;
          ours: string | null;
          theirs: string | null;
        }>;
        /** Set when the vendor could not be reached: unknown, not "no booking". */
        vendorError: string | null;
        checkedAt: string;
      }>(`/v1/admin/bookings/${id}/reconcile`, { bearer: true }),
    /**
     * What we have actually earned, from the vendor's own per-booking
     * commission. Grouped by currency throughout, so amounts settled in
     * different currencies are never added together.
     *
     * `coverage` is not decoration: it says how many confirmed bookings the
     * totals could not see, and a total read without it is a guess.
     */
    earnings: () =>
      request<{
        totals: Array<{
          currency: string;
          bookings: number;
          commissionCents: number;
          roomRevenueCents: number;
          payableCommissionCents: number;
          upcomingCommissionCents: number;
        }>;
        byMonth: Array<{
          key: string;
          label: string;
          currency: string;
          bookings: number;
          commissionCents: number;
          roomRevenueCents: number;
        }>;
        byHotel: Array<{
          key: string;
          label: string;
          currency: string;
          bookings: number;
          commissionCents: number;
          roomRevenueCents: number;
        }>;
        coverage: {
          confirmed: number;
          synced: number;
          missing: number;
          lastSyncedAt: string | null;
        };
      }>('/v1/admin/earnings', { bearer: true }),
    /**
     * The six curated properties as the vendor holds them, with a live
     * cheapest-rate probe. A hotel with no rate for the stay is the signal
     * worth having: it has quietly stopped appearing on the site.
     */
    hotels: (params: { checkIn?: string; checkOut?: string; guests?: number; refresh?: boolean } = {}) => {
      const query = new URLSearchParams();
      if (params.checkIn) query.set('checkIn', params.checkIn);
      if (params.checkOut) query.set('checkOut', params.checkOut);
      if (params.guests) query.set('guests', String(params.guests));
      if (params.refresh) query.set('refresh', 'true');
      const suffix = query.toString() ? `?${query.toString()}` : '';
      return request<{
        stay: { checkIn: string; checkOut: string; guests: number };
        currency: string;
        provider: string;
        hotels: Array<{
          id: string;
          /** The vendor's whole record, the shape the hotel-detail cache stores. */
          detail: VendorHotelRecord | null;
          reviews: Array<{
            score: number | null;
            country: string | null;
            date: string | null;
            headline: string | null;
            pros: string | null;
            cons: string | null;
          }>;
          rate: {
            hotelId: string;
            sellingPriceCents: number | null;
            netPriceCents: number | null;
            offerId: string | null;
          } | null;
          status: 'selling' | 'no_rate' | 'unknown_to_vendor';
          error: string | null;
        }>;
        /** Set when the probe itself failed: prices unknown, not absent. */
        rateError: string | null;
        checkedAt: string;
      }>(`/v1/admin/hotels${suffix}`, { bearer: true });
    },
    /**
     * The curated showcase set. Reading is open to staff; adding and removing
     * put a hotel on the public homepage, so those are admin-only server-side.
     * Add validates the id against the vendor before it can go live.
     */
    curatedHotels: {
      list: () =>
        request<{
          items: Array<{ hotelId: string; sortOrder: number; note: string | null; createdAt: string }>;
        }>('/v1/admin/curated-hotels', { bearer: true }),
      add: (hotelId: string) =>
        request<{ hotelId: string; sortOrder: number; note: string | null; createdAt: string }>(
          '/v1/admin/curated-hotels',
          { method: 'POST', body: { hotelId }, bearer: true },
        ),
      remove: (hotelId: string) =>
        request<{ removed: boolean }>(`/v1/admin/curated-hotels/${encodeURIComponent(hotelId)}`, {
          method: 'DELETE',
          bearer: true,
        }),
    },
    /**
     * Cancel the reservation at the vendor. Admin-only and irreversible: the
     * room is gone the moment they agree. The confirmation code is checked
     * against this booking server-side before anything is sent.
     */
    cancelBooking: (id: string, body: { confirmationCode: string; notifyGuest?: boolean }) =>
      request<{
        cancellation: {
          vendorBookingId: string;
          /** CANCELLED, or CANCELLED_WITH_CHARGES when the policy bit. */
          status: string;
          cancellationFeeCents: number | null;
          refundAmountCents: number | null;
          currency: string | null;
        };
        status: string;
        vendor: { status: string; confirmationCode: string | null } | null;
        guestNotified: boolean;
      }>(`/v1/admin/bookings/${id}/cancel`, { method: 'POST', body, bearer: true }),
    /**
     * Record a cancellation the vendor has already made, on our side only.
     * Touches nothing at the vendor, and the API refuses unless their live
     * record really does say cancelled.
     */
    markBookingCancelled: (id: string, body: { notifyGuest?: boolean } = {}) =>
      request<{
        status: string;
        vendor: { status: string; commissionCents?: number | null };
        guestNotified: boolean;
      }>(`/v1/admin/bookings/${id}/mark-cancelled`, { method: 'POST', body, bearer: true }),
    /**
     * Record the booking the vendor made after our own call gave up on it.
     * The mirror of markBookingCancelled: our row only, nothing sent to the
     * vendor, and the API refuses unless they really are holding it CONFIRMED.
     * Emails the guest by default, because the point is retracting the failure
     * notice they are still holding.
     */
    adoptVendorBooking: (id: string, body: { notifyGuest?: boolean } = {}) =>
      request<{
        status: string;
        vendor: {
          vendorBookingId: string;
          status: string;
          confirmationCode: string | null;
          totalPriceCents: number | null;
          currency: string | null;
          commissionCents?: number | null;
        };
        guestNotified: boolean;
      }>(`/v1/admin/bookings/${id}/adopt-vendor`, { method: 'POST', body, bearer: true }),
    /** Correct the booking holder at the vendor. Admin-only. Holder details only. */
    amendBooking: (
      id: string,
      body: { firstName: string; lastName: string; email: string; phone?: string },
    ) =>
      request<{
        amendment: {
          status: string | null;
          firstName: string | null;
          lastName: string | null;
          email: string | null;
        };
        holder: { firstName: string; lastName: string; email: string };
      }>(`/v1/admin/bookings/${id}/amend`, { method: 'POST', body, bearer: true }),
    /** Refresh the vendor snapshots now. Admin-only; costs one vendor call per booking. */
    syncVendorBookings: (limit = 25) =>
      request<{ checked: number; updated: number; failed: number; remaining: number }>(
        `/v1/admin/bookings/sync-vendor?limit=${limit}`,
        { method: 'POST', bearer: true },
      ),
    members: (page = 1, pageSize = 25, q = '') => {
      const params = new URLSearchParams();
      params.set('page', String(page));
      params.set('pageSize', String(pageSize));
      if (q.trim()) params.set('q', q.trim());
      return request<{ items: PublicUser[]; total: number; page: number; pageSize: number }>(
        `/v1/admin/members?${params.toString()}`,
        { bearer: true },
      );
    },
    /**
     * The vendor-reaching bookings, paginated and filterable by status: the
     * dashboard's Vendor transactions card without its 50-row cap. Rows are the
     * same shape the card renders, so it is reused untouched. `counts` is over
     * the whole set, not the page, so the filter always shows real totals.
     */
    transactions: (
      params: { status?: string; page?: number; pageSize?: number } = {},
    ) => {
      const q = new URLSearchParams();
      q.set('page', String(params.page ?? 1));
      q.set('pageSize', String(params.pageSize ?? 25));
      if (params.status) q.set('status', params.status);
      return request<{
        items: Array<{
          id: string;
          status: string;
          guestEmail: string | null;
          guestName: string;
          hotelName: string;
          checkIn: string;
          totalPriceCents: number;
          currency: string;
          prebookedAt: string | null;
          settledAt: string | null;
          vendorBookingId: string | null;
          vendorConfirmationCode: string | null;
          vendorPrebookId: string | null;
          vendorTransactionId: string | null;
          vendorPaymentIntentId: string | null;
          driftSeverity?: string | null;
          driftCode?: string | null;
          driftLabel?: string | null;
          driftCheckedAt?: string | null;
        }>;
        total: number;
        page: number;
        pageSize: number;
        counts: Array<{ status: string; count: number }>;
        allCount: number;
      }>(`/v1/admin/transactions?${q.toString()}`, { bearer: true });
    },
    /**
     * The exceptions queue: bookings the drift sweep found disagreeing with the
     * vendor, worst severity first.
     *
     * A read of persisted verdicts, so it costs no vendor calls and returns at
     * the speed of a query, which is the whole difference from the route it
     * replaces, where the only way to find a disagreement was to open one row at
     * a time and wait on a live vendor call for each.
     *
     * Rows come back in the Transactions shape, so the reconcile card renders
     * them untouched and a row still opens into the live comparison.
     */
    drift: (params: { severity?: string; page?: number; pageSize?: number } = {}) => {
      const q = new URLSearchParams();
      q.set('page', String(params.page ?? 1));
      q.set('pageSize', String(params.pageSize ?? 25));
      if (params.severity) q.set('severity', params.severity);
      return request<{
        items: Array<{
          id: string;
          status: string;
          guestEmail: string | null;
          guestName: string;
          hotelName: string;
          checkIn: string;
          totalPriceCents: number;
          currency: string;
          prebookedAt: string | null;
          settledAt: string | null;
          vendorBookingId: string | null;
          vendorConfirmationCode: string | null;
          vendorPrebookId: string | null;
          vendorTransactionId: string | null;
          vendorPaymentIntentId: string | null;
          driftSeverity?: string | null;
          driftCode?: string | null;
          driftLabel?: string | null;
          driftCheckedAt?: string | null;
        }>;
        total: number;
        page: number;
        pageSize: number;
        counts: Array<{ severity: string; count: number }>;
        allCount: number;
        /** Reached the vendor, never compared. Reported, never counted as clean. */
        neverChecked: number;
        lastCheckedAt: string | null;
      }>(`/v1/admin/drift?${q.toString()}`, { bearer: true });
    },
    /**
     * Compare a batch of bookings against the vendor now rather than waiting for
     * the scheduled pass. Admin-only; costs one vendor call per booking.
     */
    sweepDrift: (limit = 25) =>
      request<{
        checked: number;
        critical: number;
        warning: number;
        clear: number;
        failed: number;
        remaining: number;
      }>(`/v1/admin/drift/sweep?limit=${limit}`, { method: 'POST', bearer: true }),
    /**
     * One member with their bookings and the secretsfl.com account behind them.
     *
     * Fetched only when a row is opened: it makes a live upstream call, so the
     * list must never ask for these in bulk.
     */
    member: (id: string) =>
      request<{
        member: {
          id: string;
          email: string;
          firstName: string | null;
          lastName: string | null;
          role: string;
          /** Their id in the Secrets API. Null if this row was never linked. */
          secretsMemberId: string | null;
          createdAt: string;
          updatedAt: string;
        };
        /** The upstream account. Null if it is gone, or could not be read. */
        secrets: {
          id: string;
          username: string | null;
          email: string | null;
          firstName: string | null;
          lastName: string | null;
          avatarUrl: string | null;
          verified: boolean;
          ageVerified: boolean;
          approved: boolean;
          banned: boolean;
          createdAt: string | null;
        } | null;
        /** Set only when the upstream could not be asked. Absent ≠ unreachable. */
        secretsError: string | null;
        bookings: Array<{
          id: string;
          status: string;
          /** `email` means a guest booking that only matches on the address. */
          link: 'account' | 'email';
          hotelName: string;
          hotelCity: string | null;
          checkIn: string;
          checkOut: string;
          nights: number;
          guests: number;
          totalPriceCents: number;
          currency: string;
          vendorBookingId: string | null;
          vendorConfirmationCode: string | null;
          createdAt: string;
          settledAt: string;
        }>;
        stats: {
          total: number;
          byStatus: Array<{ status: string; count: number }>;
          /** Confirmed spend per currency; never summed across them. */
          spend: Array<{ currency: string; cents: number }>;
          firstBookingAt: string | null;
          lastBookingAt: string | null;
          matchedByEmail: number;
        };
        checkedAt: string;
      }>(`/v1/admin/members/${id}`, { bearer: true }),
    /**
     * Send a member a fresh confirmation link (admin-only). We send it ourselves
     * now, so `sent` is whether it actually went out, and nothing is sent to a
     * member who has already confirmed. `id` is our member id.
     */
    resendMemberVerification: (id: string) =>
      request<{
        sent: boolean;
        alreadyVerified: boolean;
        sentTo: string | null;
        secretsMemberId: string;
      }>(
        `/v1/admin/members/${id}/resend-verification`,
        { method: 'POST', bearer: true },
      ),
    /**
     * The vendor webhook events we have handled, newest first. A read; staff may
     * see it. Each row carries the booking it resolved to. `counts` is per
     * outcome across the whole set, so a lone unmatched event stands out.
     */
    webhooks: (params: { page?: number; pageSize?: number } = {}) => {
      const q = new URLSearchParams();
      q.set('page', String(params.page ?? 1));
      q.set('pageSize', String(params.pageSize ?? 25));
      return request<{
        items: Array<{
          eventId: string;
          provider: string;
          eventName: string;
          vendorBookingId: string | null;
          bookingId: string | null;
          outcome: string;
          receivedAt: string;
          hotelName: string | null;
          guestName: string | null;
          bookingStatus: string | null;
        }>;
        total: number;
        page: number;
        pageSize: number;
        counts: Array<{ outcome: string; count: number }>;
      }>(`/v1/admin/webhooks?${q.toString()}`, { bearer: true });
    },
    /**
     * Find a booking by guest email/name, vendor confirmation code, vendor
     * booking/transaction id, or our own id. Rows match the Transactions shape,
     * so a hit opens into the same reconciliation panel. Capped server-side;
     * `truncated` says the term was too broad.
     */
    searchBookings: (q: string) =>
      request<{
        items: Array<{
          id: string;
          status: string;
          guestEmail: string | null;
          guestName: string;
          hotelName: string;
          checkIn: string;
          totalPriceCents: number;
          currency: string;
          prebookedAt: string | null;
          settledAt: string | null;
          vendorBookingId: string | null;
          vendorConfirmationCode: string | null;
          vendorPrebookId: string | null;
          vendorTransactionId: string | null;
          vendorPaymentIntentId: string | null;
        }>;
        truncated: boolean;
      }>(`/v1/admin/bookings/search?q=${encodeURIComponent(q)}`, { bearer: true }),
    /**
     * The trail of irreversible admin actions (cancel / amend / mark-cancelled),
     * newest first. Admin-only. Each row carries the booking it touched so it
     * reads without a second lookup.
     */
    audit: (params: { page?: number; pageSize?: number; bookingId?: string } = {}) => {
      const q = new URLSearchParams();
      q.set('page', String(params.page ?? 1));
      q.set('pageSize', String(params.pageSize ?? 25));
      if (params.bookingId) q.set('bookingId', params.bookingId);
      return request<{
        items: Array<{
          id: string;
          actorEmail: string;
          action: string;
          bookingId: string | null;
          summary: string;
          metadata: unknown;
          createdAt: string;
          hotelName: string | null;
          guestName: string | null;
          bookingStatus: string | null;
        }>;
        total: number;
        page: number;
        pageSize: number;
      }>(`/v1/admin/audit?${q.toString()}`, { bearer: true });
    },
    availabilityCache: () =>
      request<{
        total: number;
        fresh: number;
        stale: number;
        provider: string;
        recent: Array<{ queryText: string; expiresAt: string; createdAt: string }>;
        /** How much of what guests are shown the hotel-detail cache covers. */
        hotelDetail: {
          rows: number;
          withDetail: number;
          unknownToVendor: number;
          expired: number;
          oldestWriteAt: string | null;
          newestWriteAt: string | null;
          shown: number;
          shownWithoutAnswer: number;
          windowDays: number;
        };
      }>('/v1/admin/availability-cache', { bearer: true }),
    /**
     * Ask the vendor again about every hotel shown lately, in the background.
     * Admin-only. Rows are rewritten in place, so no card goes blank meanwhile.
     */
    refetchHotelDetails: () =>
      request<{ queued: number }>('/v1/admin/hotel-detail-cache/refetch', {
        method: 'POST',
        bearer: true,
      }),
  },
  // Public content reads for the template pages. Slides + FAQs are admin-managed;
  // Gallery, News, and Events come from the SecretsFL API.
  content: {
    slides: () => request<Slide[]>('/v1/content/slides'),
    faqs: () => request<Faq[]>('/v1/content/faqs'),
    gallery: () => request<SecretsGalleryImage[]>('/v1/content/gallery'),
    news: () => request<SecretsNewsArticle[]>('/v1/content/news'),
    newsItem: (id: string) => request<SecretsNewsArticle>(`/v1/content/news/${id}`),
    events: () => request<SecretsEvent[]>('/v1/content/events'),
    eventItem: (id: string) => request<SecretsEvent>(`/v1/content/events/${id}`),
  },
  // Admin CRUD for the managed content collections.
  adminContent: {
    slides: {
      list: () => request<Slide[]>('/v1/admin/content/slides', { bearer: true }),
      create: (body: SlideInput) =>
        request<Slide>('/v1/admin/content/slides', { method: 'POST', body, bearer: true }),
      update: (id: string, body: SlideInput) =>
        request<Slide>(`/v1/admin/content/slides/${id}`, { method: 'PUT', body, bearer: true }),
      remove: (id: string) =>
        request<{ deleted: boolean }>(`/v1/admin/content/slides/${id}`, {
          method: 'DELETE',
          bearer: true,
        }),
    },
    gallery: {
      list: () => request<GalleryItem[]>('/v1/admin/content/gallery', { bearer: true }),
      create: (body: GalleryItemInput) =>
        request<GalleryItem>('/v1/admin/content/gallery', { method: 'POST', body, bearer: true }),
      update: (id: string, body: GalleryItemInput) =>
        request<GalleryItem>(`/v1/admin/content/gallery/${id}`, {
          method: 'PUT',
          body,
          bearer: true,
        }),
      remove: (id: string) =>
        request<{ deleted: boolean }>(`/v1/admin/content/gallery/${id}`, {
          method: 'DELETE',
          bearer: true,
        }),
    },
    faqs: {
      list: () => request<Faq[]>('/v1/admin/content/faqs', { bearer: true }),
      create: (body: FaqInput) =>
        request<Faq>('/v1/admin/content/faqs', { method: 'POST', body, bearer: true }),
      update: (id: string, body: FaqInput) =>
        request<Faq>(`/v1/admin/content/faqs/${id}`, { method: 'PUT', body, bearer: true }),
      remove: (id: string) =>
        request<{ deleted: boolean }>(`/v1/admin/content/faqs/${id}`, {
          method: 'DELETE',
          bearer: true,
        }),
    },
  },
};
